import React, { useMemo, useRef, useState } from 'react';
import * as Location from './fields/Location';
import * as Dates from './fields/Dates';
import * as Occupancy from './fields/Occupancy';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { SubmitButton } from './components/SubmitButton';
import { FormFieldsWrapper } from './components/FormFieldsWrapper';
import type { StaysSearchTerms } from '@apps/stays/types';
import { Box, Divider, Flex, JoyKitThemeProvider } from '@withjoy/joykit';
import { pxToRem, defaultTheme } from '@withjoy/joykit/theme';
import { convertToDateRangeFromDateParts } from '@shared/utils/dateHelpers';
import { useChildAgeValidation } from '@apps/stays/hooks/useChildAgeValidation';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTimeout } from '@shared/hooks/useTimeout';
import { isEqual } from 'lodash-es';
import { getValidCoordinates } from '@shared/core/places';
import { addError } from '@shared/utils/logger';
import type { SearchFormConfig } from './useSearchFormConfig';
import { DEFAULT_CONFIG, SearchFormConfigContext, useSearchFormConfig } from './useSearchFormConfig';

export { useSearchFormConfig };

const LOCATION_FIELD_WIDTH = pxToRem(260);
const OCCUPANCY_FIELD_WIDTH = pxToRem(170);
const COMPACT_VERSION_SUBMIT_BUTTON_WIDTH = pxToRem(100);

const DAYS_OFFSET_FOR_DEFAULT_END_DATE = 3;
const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;

interface Props {
  onSubmit: (value: StaysSearchTerms) => void;
  initialValue?: Partial<StaysSearchTerms>;
  config?: Readonly<{
    dateRangeEnabled?: boolean;
    occupancySelectorEnabled?: boolean;
    isOpenedFormView?: boolean;
    isCompactVersion?: boolean;
  }>;
  /** Optional accent color (cash-fund gold): golds the search CTA + recolors the brand-primary token
   *  (occupancy steppers, calendar selection) for the whole search form. Defaults to the brand color. */
  accentColor?: string;
}

export const LocationDatesOccupants = (props: Props) => {
  const [{ today, threeDaysFromNow }] = useState(() => {
    const now = new Date();
    return {
      today: now,
      threeDaysFromNow: new Date(now.getTime() + DAYS_OFFSET_FOR_DEFAULT_END_DATE * MILLISECONDS_IN_A_DAY),
    };
  });

  const [isAnyFieldFocused, setIsAnyFieldFocused] = useState(false);
  const blurTimeout = useTimeout();

  const searchForm: SearchFormConfig = {
    dateRangeEnabled: props.config?.dateRangeEnabled ?? DEFAULT_CONFIG.dateRangeEnabled,
    occupancySelectorEnabled: props.config?.occupancySelectorEnabled ?? DEFAULT_CONFIG.occupancySelectorEnabled,
    isOpenedFormView: props.config?.isOpenedFormView ?? DEFAULT_CONFIG.isOpenedFormView,
    isCompactVersion: props.config?.isCompactVersion ?? DEFAULT_CONFIG.isCompactVersion,
    isAnyFieldFocused,
  };

  const formHeight = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled ? 'fit-content' : '100%';
  const showDatesAndOccupancies = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled;

  const validation = useChildAgeValidation();

  const schema = useMemo(
    () =>
      Yup.object({
        location: Location.schema,
        dates: Dates.schema,
        occupancy: Occupancy.createSchema(validation),
      }),
    [validation],
  );

  const handleOnFocus = useEventCallback(() => {
    blurTimeout.clear();
    setIsAnyFieldFocused(true);
  });

  const handleOnBlur = useEventCallback(() => {
    // This prevents jumping when switching between fields
    blurTimeout.set(() => {
      setIsAnyFieldFocused(false);
    }, 100);
  });

  return (
    <AccentThemeProvider accentColor={props.accentColor}>
      <SearchFormConfigContext.Provider value={searchForm}>
        <Formik
          initialValues={{
            location: {
              displayName: props.initialValue?.destination ?? '',
              latitude: props.initialValue?.lat,
              longitude: props.initialValue?.lng,
              googlePlaceId: props.initialValue?.googlePlaceId,
            },
            // what I would love to do is use 'satisfies' the type of Dates.initialValue
            dates: convertToDateRangeFromDateParts(props.initialValue?.startDate, props.initialValue?.endDate),
            // TODO: fix childrenAges type
            occupancy: props.initialValue?.rooms || Occupancy.initialValue,
          }}
          onSubmit={value => {
            const validCoordinates = getValidCoordinates({ latitude: value.location.latitude, longitude: value.location.longitude });
            if (!validCoordinates) {
              addError('Submit blocked: invalid coordinates', { context: 'LocationDatesOccupants', data: value.location });
              return;
            }

            const { startDate, endDate } = value.dates;
            // DateRange can set endDate to null on single-click; guard defensively.
            if (!startDate || !endDate) {
              addError('Submit blocked: missing dates', { context: 'LocationDatesOccupants', data: value.dates });
              return;
            }

            props.onSubmit({
              destination: value.location.displayName,
              lat: validCoordinates.lat,
              lng: validCoordinates.lng,
              googlePlaceId: value.location.googlePlaceId,
              startDate: {
                year: startDate.getFullYear(),
                month: startDate.getMonth() + 1,
                day: startDate.getDate(),
              },
              endDate: {
                year: endDate.getFullYear(),
                month: endDate.getMonth() + 1,
                day: endDate.getDate(),
              },
              rooms: value.occupancy.map(room => ({
                adults: room.adults,
                childrenAges: room.childrenAges.filter((age): age is number => age !== null),
              })),
            });
          }}
          validationSchema={schema}
        >
          <Form
            name="search-fields"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: formHeight }}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
          >
            <ResetFormOnNewInitialValue initialValue={props.initialValue} />
            {searchForm.isOpenedFormView ? (
              <>
                <Flex
                  paddingX={6}
                  paddingBottom={6}
                  width={'100%'}
                  flexDirection={'column'}
                  height={formHeight}
                  justifyContent={!showDatesAndOccupancies ? 'space-between' : 'flex-start'}
                >
                  <Location.Fields fieldsId={'location'} />
                  {showDatesAndOccupancies ? (
                    <>
                      {searchForm.dateRangeEnabled && (
                        <Dates.Fields
                          fieldsId={'dates'}
                          defaultStartDate={today}
                          defaultEndDate={threeDaysFromNow}
                        />
                      )}
                      {searchForm.occupancySelectorEnabled && <Occupancy.Fields fieldsId={'occupancy'} />}
                    </>
                  ) : null}
                </Flex>
                <SubmitButton backgroundColor={props.accentColor} />
              </>
            ) : (
              <>
                {/* Mobile View - Vertically Stacked (or forced mobile version) */}
                <Flex
                  display={searchForm.isCompactVersion ? 'flex' : { _: 'flex', md: 'none' }}
                  width={'100%'}
                  flexDirection={'column'}
                  backgroundColor="white"
                  border="solid 2px"
                  borderColor="mono3"
                  borderRadius={4}
                  gap={4}
                  padding={4}
                >
                  <Location.Fields fieldsId={'location'} />
                  {showDatesAndOccupancies && (
                    <>
                      {searchForm.dateRangeEnabled && (
                        <Dates.Fields
                          fieldsId={'dates'}
                          defaultStartDate={today}
                          defaultEndDate={threeDaysFromNow}
                        />
                      )}
                      {searchForm.occupancySelectorEnabled && <Occupancy.Fields fieldsId={'occupancy'} />}
                    </>
                  )}
                  <SubmitButton backgroundColor={props.accentColor} />
                </Flex>

                {/* Desktop View - Horizontally Aligned (hidden when isCompactVersion is true) */}
                {!searchForm.isCompactVersion && (
                  <Flex
                    display={{ _: 'none', md: 'flex' }}
                    alignItems="center"
                    gap={3}
                  >
                    {/* 9999px pill radius keeps inner elements visually concentric (outer_radius >= inner_radius + padding). */}
                    <FormFieldsWrapper borderRadius="9999px">
                      <Box
                        gridArea={'location'}
                        height={pxToRem(24)}
                        minWidth={LOCATION_FIELD_WIDTH}
                      >
                        <Location.Fields fieldsId={'location'} />
                      </Box>
                      {showDatesAndOccupancies ? (
                        <>
                          {searchForm.dateRangeEnabled && (
                            <>
                              <Divider
                                orientation="vertical"
                                height={pxToRem(24)}
                              />
                              <Box
                                gridArea={'dates'}
                                height={pxToRem(24)}
                              >
                                <Dates.Fields
                                  fieldsId={'dates'}
                                  defaultStartDate={today}
                                  defaultEndDate={threeDaysFromNow}
                                />
                              </Box>
                              <Divider
                                orientation="vertical"
                                height={pxToRem(24)}
                              />
                            </>
                          )}
                          {searchForm.occupancySelectorEnabled && (
                            <Box
                              gridArea={'occupancy'}
                              minWidth={OCCUPANCY_FIELD_WIDTH}
                            >
                              <Occupancy.Fields fieldsId={'occupancy'} />
                            </Box>
                          )}
                        </>
                      ) : null}
                      <Box
                        gridArea={'submit'}
                        display="flex"
                        justifyContent="flex-end"
                        width={COMPACT_VERSION_SUBMIT_BUTTON_WIDTH}
                      >
                        <SubmitButton backgroundColor={props.accentColor} />
                      </Box>
                    </FormFieldsWrapper>
                  </Flex>
                )}
              </>
            )}
          </Form>
        </Formik>
      </SearchFormConfigContext.Provider>
    </AccentThemeProvider>
  );
};

// Scopes an accent-color override (e.g. cash-fund gold) to the search form only. Recolors both the raw
// `newUIBrandPrimary` token (occupancy steppers + calendar selection read it directly) and the resolved
// `buttonPrimary*` tokens (the primary buttons — Search + Apply — read those, not the raw token).
// Touches no shared leaf components; inert when no accent is set — guest/admin/ota unchanged.
const AccentThemeProvider = ({ accentColor, children }: { accentColor?: string; children: React.ReactNode }) => {
  const theme = useMemo(
    () =>
      accentColor
        ? {
            ...defaultTheme,
            colors: {
              ...defaultTheme.colors,
              newUIBrandPrimary: accentColor,
              buttonPrimaryFill: accentColor,
              buttonPrimaryHover: accentColor,
              buttonPrimaryActive: accentColor,
            },
          }
        : null,
    [accentColor],
  );
  return theme ? <JoyKitThemeProvider theme={theme}>{children}</JoyKitThemeProvider> : <>{children}</>;
};

const ResetFormOnNewInitialValue = (props: Pick<Props, 'initialValue'>) => {
  const formik = useFormikContext();
  const prevInitialValueRef = useRef(props.initialValue);

  React.useEffect(() => {
    if (isEqual(prevInitialValueRef.current, props.initialValue)) {
      return;
    }
    prevInitialValueRef.current = props.initialValue;

    formik.resetForm({
      values: {
        location: {
          displayName: props.initialValue?.destination,
          latitude: props.initialValue?.lat,
          longitude: props.initialValue?.lng,
          googlePlaceId: props.initialValue?.googlePlaceId || '',
        },
        dates: convertToDateRangeFromDateParts(props.initialValue?.startDate, props.initialValue?.endDate),
        occupancy: props.initialValue?.rooms || Occupancy.initialValue,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);
  return null;
};
